import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import axios from 'axios';
import { BaseURL, BucketURL } from '../../ApiSetup/Api';

const PostGenerator = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loginPromptOpen, setLoginPromptOpen] = useState(false);

    useEffect(() => {
        fetchPublicCategories();
    }, []);

    const fetchPublicCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BaseURL}public/posts/categories`);
            if (response?.data?.success) {
                setCategories(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTemplates = async (categoryId) => {
        setLoadingTemplates(true);
        try {
            const response = await axios.get(`${BaseURL}public/posts/templates/${categoryId}`);
            if (response?.data?.success) {
                setTemplates(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleCategoryClick = (category) => {
        if (category.accessLevel !== 'all') {
            setLoginPromptOpen(true);
            return;
        }
        setSelectedCategory(category);
        fetchTemplates(category._id);
    };

    const handleTemplateClick = (template) => {
        setSelectedTemplate(template);
        setPreviewOpen(true);
    };

    const handleDownload = async () => {
        if (!selectedTemplate) return;
        
        // For public templates, generate generic post (no photo)
        try {
            const response = await axios.post(`${BaseURL}public/posts/generate`, {
                templateId: selectedTemplate._id
            }, { responseType: 'blob' });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ppsk_post_${Date.now()}.png`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading:', error);
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '/placeholder-image.png';
        if (path.startsWith('http')) return path;
        return `${BucketURL}${path}`;
    };

    return (
        <Box sx={{ py: 6, bgcolor: '#f5f5f5', minHeight: '80vh' }}>
            <Container maxWidth="lg">
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            color: '#950904',
                            mb: 2,
                            fontSize: isMobile ? '1.8rem' : '2.5rem'
                        }}
                    >
                        🎨 PPSK Post Generator
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                        Create beautiful personalized posts and share them on social media
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Categories Section */}
                        {!selectedCategory ? (
                            <>
                                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                                    Select a Category
                                </Typography>
                                <Grid container spacing={3}>
                                    {categories.map((category) => (
                                        <Grid item xs={6} sm={4} md={3} key={category._id}>
                                            <Card
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: 4,
                                                    },
                                                    position: 'relative',
                                                    opacity: category.accessLevel !== 'all' ? 0.7 : 1,
                                                }}
                                                onClick={() => handleCategoryClick(category)}
                                            >
                                                {category.accessLevel !== 'all' && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            bgcolor: 'rgba(0,0,0,0.6)',
                                                            borderRadius: '50%',
                                                            p: 0.5,
                                                        }}
                                                    >
                                                        <LockIcon sx={{ color: 'white', fontSize: 16 }} />
                                                    </Box>
                                                )}
                                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography variant="h2" sx={{ mb: 1 }}>
                                                        {category.icon}
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {category.name}
                                                    </Typography>
                                                    {category.nameGuj && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {category.nameGuj}
                                                        </Typography>
                                                    )}
                                                    <Chip
                                                        label={`${category.templateCount || 0} Templates`}
                                                        size="small"
                                                        sx={{ mt: 1 }}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Login CTA */}
                                <Box
                                    sx={{
                                        mt: 6,
                                        p: 4,
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        textAlign: 'center',
                                        boxShadow: 1,
                                    }}
                                >
                                    <Typography variant="h6" gutterBottom>
                                        🔒 Want personalized posts with your photo?
                                    </Typography>
                                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                                        Register or login to access all post types and generate posts with your photo
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ bgcolor: '#950904', '&:hover': { bgcolor: '#7a0703' } }}
                                        onClick={() => navigate('/admin/login')}
                                    >
                                        Login / Register
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <>
                                {/* Templates Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Button onClick={() => { setSelectedCategory(null); setTemplates([]); }}>
                                        ← Back to Categories
                                    </Button>
                                </Box>

                                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                                    {selectedCategory.icon} {selectedCategory.name} Templates
                                </Typography>

                                {loadingTemplates ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : templates.length > 0 ? (
                                    <Grid container spacing={3}>
                                        {templates.map((template) => (
                                            <Grid item xs={12} sm={6} md={4} key={template._id}>
                                                <Card
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 4,
                                                        },
                                                    }}
                                                    onClick={() => handleTemplateClick(template)}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        height="250"
                                                        image={getImageUrl(template.previewImage)}
                                                        alt={template.name}
                                                        sx={{ objectFit: 'cover' }}
                                                    />
                                                    <CardContent>
                                                        <Typography variant="h6" fontWeight="bold">
                                                            {template.name}
                                                        </Typography>
                                                        {template.nameGuj && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {template.nameGuj}
                                                            </Typography>
                                                        )}
                                                    </CardContent>
                                                    <CardActions>
                                                        <Button
                                                            size="small"
                                                            startIcon={<DownloadIcon />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTemplate(template);
                                                                handleDownload();
                                                            }}
                                                        >
                                                            Download
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Alert severity="info">No templates available in this category</Alert>
                                )}
                            </>
                        )}
                    </>
                )}
            </Container>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{selectedTemplate?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center' }}>
                        <img
                            src={getImageUrl(selectedTemplate?.previewImage)}
                            alt={selectedTemplate?.name}
                            style={{ maxWidth: '100%', maxHeight: '60vh' }}
                        />
                    </Box>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        This is a generic template preview. Login to generate personalized posts with your photo!
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        sx={{ bgcolor: '#950904' }}
                    >
                        Download Template
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Login Prompt Dialog */}
            <Dialog open={loginPromptOpen} onClose={() => setLoginPromptOpen(false)}>
                <DialogTitle>🔒 Login Required</DialogTitle>
                <DialogContent>
                    <Typography>
                        This category requires you to be logged in. Register or login to access personalized posts with your photo!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLoginPromptOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/admin/login')}
                        sx={{ bgcolor: '#950904' }}
                    >
                        Login / Register
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PostGenerator;
